import { fromPairs } from 'lodash';

export type ID = string | number; // TODO: consider having it only as "string"

export const isNegativeId = (id?: ID): boolean => {
  if (!id) {
    return false;
  }

  return Number(id) < 0;
};

export const isPositiveId = (id?: ID): boolean => {
  if (!id) {
    return false;
  }

  return !isNegativeId(id);
};

/**
 * Base interface for entities with an ID
 */
export interface Entity {
  id: ID;
}

/**
 * Base interface for entities with an ID
 */
export interface EntityWithName extends Entity {
  name: string;
}

/**
 * Options for merging entities into arrays
 */
interface MergeEntityOptions {
  /** If true, the new item will be added at the beginning of the array */
  leading?: boolean;
}

/**
 * Converts an array of entities to an object map keyed by entity ID
 * @template T - The type of entity extending the Entity interface
 * @param array - Array of entities to convert
 * @returns Object map with entity IDs as keys
 *
 * @example
 * ```typescript
 * const users = [
 *   { id: 1, name: 'Alice' },
 *   { id: 2, name: 'Bob' }
 * ];
 * const userMap = entityArrayToObjectMap(users);
 * // Result: { 1: { id: 1, name: 'Alice' }, 2: { id: 2, name: 'Bob' } }
 * ```
 */
export const entityArrayToObjectMap = <T extends Entity>(array: T[]): Record<ID, T> => {
  const pairs = array.map((obj) => [obj.id, obj]);
  return fromPairs(pairs);
};

/**
 * Merges an entity into an array, either updating an existing entity or adding a new one
 * @template T - The type of entity extending the Entity interface
 * @param array - The array to merge the entity into
 * @param itemToMerge - The entity to merge
 * @param options - Optional configuration for the merge operation
 * @returns New array with the merged entity
 *
 * @example
 * ```typescript
 * const users = [
 *   { id: 1, name: 'Alice' },
 *   { id: 2, name: 'Bob' }
 * ];
 *
 * // Update existing user
 * const updatedUsers = mergeEntityIntoEntityArray(users, { id: 1, name: 'Alice Updated' });
 *
 * // Add new user at the end
 * const newUsers = mergeEntityIntoEntityArray(users, { id: 3, name: 'Charlie' });
 *
 * // Add new user at the beginning
 * const newUsersLeading = mergeEntityIntoEntityArray(users, { id: 3, name: 'Charlie' }, { leading: true });
 * ```
 */
export const mergeEntityIntoEntityArray = <T extends Entity>(
  array: T[],
  itemToMerge?: T,
  options?: MergeEntityOptions
): T[] => {
  if (!itemToMerge) {
    return array;
  }

  const itemExists = array.some((t) => t.id === itemToMerge.id);
  if (itemExists) {
    return array.map((item) => (item.id === itemToMerge.id ? { ...item, ...itemToMerge } : item));
  }

  if (options?.leading) {
    return [itemToMerge, ...array];
  }

  return [...array, itemToMerge];
};

export const mergeEntitiesIntoEntityArray = <T extends Entity>(
  array: T[],
  itemsToMerge: T[],
  options?: MergeEntityOptions
): T[] => {
  if (options?.leading) {
    return itemsToMerge.reduceRight(
      (acc: T[], item: T) => mergeEntityIntoEntityArray(acc, item, options),
      array
    );
  } else {
    return itemsToMerge.reduce(
      (acc: T[], item: T) => mergeEntityIntoEntityArray(acc, item, options),
      array
    );
  }
};

/**
 * Adds a new element after a specified element in an array
 * @template T - The type of elements in the array
 * @param array - The array to modify
 * @param newElement - The element to add
 * @param afterElement - The element to add after
 * @returns New array with the element added in the specified position
 *
 * @example
 * ```typescript
 * const numbers = [1, 2, 4];
 * const result = arrayAddAfter(numbers, 3, 2);
 * // Result: [1, 2, 3, 4]
 * ```
 */
export const arrayAddAfter = <T>(array: T[], newElement: T, afterElement: T): T[] => {
  const afterIndex = array.indexOf(afterElement);
  if (afterIndex >= 0) {
    const beforeElements = array.slice(0, afterIndex + 1);
    const afterElements = array.slice(afterIndex + 1);
    return [...beforeElements, newElement, ...afterElements];
  }
  return [...array, newElement];
};

/**
 * Swaps the positions of two elements in an array
 * @template T - The type of elements in the array
 * @param array - The array to modify
 * @param element1 - First element to swap
 * @param element2 - Second element to swap
 * @returns New array with the elements swapped, or original array if either element not found
 *
 * @example
 * ```typescript
 * const numbers = [1, 2, 3, 4];
 * const result = arraySwitchElements(numbers, 2, 4);
 * // Result: [1, 4, 3, 2]
 * ```
 */
export const arraySwitchElements = <T>(array: T[], element1: T, element2: T): T[] => {
  const element1Index = array.indexOf(element1);
  const element2Index = array.indexOf(element2);
  if (element1Index === -1 || element2Index === -1) {
    return array;
  }
  const newArray = [...array];
  newArray[element2Index] = element1;
  newArray[element1Index] = element2;
  return newArray;
};
